import { useCallback, useMemo, useRef } from 'react';
import { AppEventsContext } from './hooks/useAppEventContext';
import { useAppEventExtension } from './hooks/useAppEventExtension';
import { useAppEventQueueRelease } from './hooks/useAppEventQueueRelease';
import { useAppEventUICollector } from './hooks/useAppEventUICollector';
import { useIdleAppEvents } from './hooks/useIdleAppEvents';
import { useLogRocketLinkedEvents } from './hooks/useLogRocketLinkedEvents';
import { useNavigationAppEvents } from './hooks/useNavigationAppEvents';
import { serializeAppEvent } from './utils/serialezeAppEvent';

window.TA_APP_EVENTS = [];

export const AppEventsProvider = ({ children }) => {
    const listeners = useRef([]);
    const register = useCallback(callback => {
        listeners.current.push(callback);
        return () => {
            listeners.current = listeners.current.filter(listener => listener !== callback);
        };
    }, []);
    const { pushToQueue } = useAppEventQueueRelease(register);
    const extendEvent = useAppEventExtension();

    const push = useCallback(
        async event => {
            const fullEvent = await extendEvent({ event });

            fullEvent.event = serializeAppEvent(event);

            listeners.current.forEach(listener => listener(fullEvent));

            if (!fullEvent.__skip_app_event) {
                pushToQueue(fullEvent);
            }

            window.TA_APP_EVENTS.push(fullEvent);
        },
        [extendEvent, pushToQueue]
    );

    useAppEventUICollector(push);
    useIdleAppEvents(push);
    useNavigationAppEvents(push);
    useLogRocketLinkedEvents(register);

    const value = useMemo(() => ({ register, push }), [register, push]);

    return <AppEventsContext.Provider value={value}>{children}</AppEventsContext.Provider>;
};
